import { useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { ShieldCheck, AlertTriangle, Building2, Search, Info } from "lucide-react";
import { EU_COUNTRIES } from "../data/vatData";
import { useI18n } from "../i18n/I18nContext";
import { trackViesValidated } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VatNumberValidator() {
    const { t } = useI18n();
    const [country, setCountry] = useState("DE");
    const [vat, setVat] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const validate = async (e) => {
        e?.preventDefault();
        if (!vat.trim()) {
            setError(t("vies.errorEmpty"));
            setResult(null);
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await axios.get(`${API}/vies/check`, {
                params: { country, vat: vat.trim() },
            });
            setResult(res.data);
            trackViesValidated(country, vat.trim(), !!res.data?.valid);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : t("vies.errorService"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            data-testid="vies-section"
            className="max-w-7xl mx-auto px-4 sm:px-6 py-10"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                <div className="lg:col-span-4">
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">
                        {t("vies.eyebrow")}
                    </div>
                    <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
                        {t("vies.titleLine1")}
                        <span className="text-[#10B981]">{t("vies.titleHighlight")}</span>
                        {t("vies.titleLine2")}
                    </h2>
                    <p className="mt-3 text-slate-600 leading-relaxed">{t("vies.intro")}</p>
                </div>

                <div
                    data-testid="vies-card"
                    className="lg:col-span-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
                >
                    <div className="flex items-center gap-2 mb-5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-700">
                            <ShieldCheck className="h-4 w-4" />
                        </span>
                        <h3 className="font-display text-lg font-bold text-slate-900">
                            {t("vies.cardTitle")}
                        </h3>
                    </div>

                    <form onSubmit={validate} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-3 items-end">
                        <div>
                            <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                {t("vies.country")}
                            </Label>
                            <Select value={country} onValueChange={setCountry}>
                                <SelectTrigger
                                    data-testid="vies-country"
                                    className="h-11 mt-2 border-slate-200"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {EU_COUNTRIES.map((c) => (
                                        <SelectItem key={c.code} value={c.code}>
                                            <span className="mr-2">{c.flag}</span> {c.code}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold tracking-[0.16em] uppercase text-slate-500">
                                {t("vies.vatNumber")}
                            </Label>
                            <Input
                                data-testid="vies-vat-input"
                                value={vat}
                                onChange={(e) => setVat(e.target.value)}
                                placeholder="123456789"
                                className="h-11 mt-2 border-slate-200 font-mono focus-visible:ring-[#10B981] focus-visible:border-[#10B981]"
                            />
                        </div>

                        <Button
                            data-testid="vies-validate-btn"
                            type="submit"
                            disabled={loading}
                            className="h-11 bg-[#10B981] hover:bg-emerald-600 text-white font-semibold"
                        >
                            <Search className="h-4 w-4 mr-1.5" />
                            {loading ? t("vies.validating") : t("vies.validate")}
                        </Button>
                    </form>

                    {error && (
                        <div
                            data-testid="vies-error"
                            className="mt-5 rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm flex gap-2.5 items-start"
                        >
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <div>{error}</div>
                        </div>
                    )}

                    {result && (
                        <div className="mt-5 space-y-3">
                            <div
                                data-testid={result.valid ? "vies-result-valid" : "vies-result-invalid"}
                                className={`rounded-md border p-4 ${
                                    result.valid
                                        ? "border-emerald-200 bg-emerald-50"
                                        : "border-rose-200 bg-rose-50"
                                }`}
                            >
                                <div className="flex items-center gap-2 font-semibold">
                                    <span className="text-lg">{result.valid ? "🟢" : "🔴"}</span>
                                    <span className={result.valid ? "text-emerald-900" : "text-rose-900"}>
                                        {result.valid ? t("vies.valid") : t("vies.invalid")}
                                    </span>
                                    <span className="ml-auto font-mono text-xs text-slate-500">
                                        {result.countryCode} {result.vatNumber}
                                    </span>
                                </div>
                                {result.valid && result.name && (
                                    <div className="mt-3 flex gap-2.5 text-sm text-slate-700">
                                        <Building2 className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
                                        <div>
                                            <div className="font-semibold text-slate-900">{result.name}</div>
                                            {result.address && (
                                                <div className="mt-0.5 whitespace-pre-line text-slate-600 text-xs leading-relaxed">
                                                    {result.address}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {result.error && (
                                    <div className="mt-2 text-xs text-slate-500">{result.error}</div>
                                )}
                            </div>

                            {result.valid && (
                                <div
                                    data-testid="vies-reverse-charge"
                                    className="rounded-md border border-[#10B981]/30 bg-emerald-50/60 p-4 flex gap-2.5 items-start"
                                >
                                    <Info className="h-4 w-4 text-[#10B981] mt-0.5 shrink-0" />
                                    <div>
                                        <div className="font-semibold text-emerald-900">
                                            {t("vies.reverseChargeTitle")}
                                        </div>
                                        <p className="mt-1 text-sm text-slate-700 leading-relaxed">
                                            {t("vies.reverseChargeBody", {
                                                country: result.countryCode,
                                            })}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
